import i18n from 'i18next';
import { FunctionComponent } from 'react';
import { Trans } from 'react-i18next';

import useElasticSearch from '../../hooks/useElasticSearch';
import useSelectedObjectState, { compareSelectInput } from '../../hooks/useSelectedObjectState';
import { getNormalizedQueryStringParameter } from '../../utils/queryString';
import ExinMuiCircularProgress from '../styled/ExinMuiCircularProgress';
import ExinSelect from '../styled/ExinSelect';
import ExinVoucherButton from '../styled/ExinVoucherButton';
import { Center } from './StepBookingCenter';
import { Certification } from './StepCertification';

export type Country = {
  value: string;
  label: string;
  /**
   * Remember the services available for this language, so it doesn't need to be queried _again_ in <StepBookingCenter>
   */
  services: Array<Center>;
};

/**
 * Takes a raw server response and transforms it into the data that our components prefer.
 */
function transformServerResponse(garbage: unknown): Array<Country> {
  const serviceMap: { [index: string]: any } = {
    OLP: {
      label: i18n.t('examSeviceOlp'),
      tooltip: (
        <Trans i18nKey="examSeviceOlpTooltip">
          Take your exam online through our exam service (video or live proctored).
        </Trans>
      ),
    },
    ERP: {
      label: i18n.t('examSeviceErp'),
      tooltip: (
        <Trans i18nKey="examSeviceErpTooltip">Take your exam at a Eureka exam center.</Trans>
      ),
    },
  };

  return Object.values(
    (garbage as any).hits.hits
      // Flatten all results, in case there were multiple certifications with the same ID
      .reduce(
        (accum: Country[], hit: any) =>
          hit._source.exam_services ? [...accum, ...hit._source.exam_services] : accum,
        []
      )
      // Next, collate services by country code
      .reduce((map: Record<string, Country>, exam_service: any) => {
        if (!map[exam_service.country_id]) {
          map[exam_service.country_id] = {
            // Cast numeric identifier to string so it plays nice with MUI form elements
            value: String(exam_service.country_id),
            label: exam_service.country,
            services: [],
          };
        }
        const index = String(exam_service.exam_service);
        map[exam_service.country_id].services.push({
          // Cast numeric identifier to string so it plays nice with MUI form elements
          value: String(exam_service.exam_service_id),
          label: serviceMap?.[index]?.label || index,
          tooltip: serviceMap?.[index]?.tooltip || '',
        });
        return map;
      }, {})
  ).sort((a: any, b: any) => a.label.localeCompare(b.label)) as Country[];
}

const StepBookingCountry: FunctionComponent<{
  onChange(country: Country): void;
  certification: Certification;
}> = ({ onChange, certification }) => {
  const { isLoading, response: countries } = useElasticSearch(
    {
      query: {
        bool: {
          must: [
            {
              term: {
                'post_type.raw': 'ex_certification',
              },
            },
            {
              term: {
                post_id: parseInt(certification.value, 10),
              },
            },
          ],
        },
      },
      size: 10000,
      _source: [
        'exam_services.country',
        'exam_services.country_id',
        'exam_services.exam_service',
        'exam_services.exam_service_id',
      ],
    },
    transformServerResponse
  );

  const [selectedCountry, setSelectedCountryValue] = useSelectedObjectState(
    countries,
    getNormalizedQueryStringParameter('country'),
    { onChange, comparator: compareSelectInput }
  );

  if (isLoading) {
    return <ExinMuiCircularProgress />;
  }

  if (!countries?.length) {
    return (
      <>
        <Trans i18nKey="messageNoCountries">No countries found</Trans>

        <p style={{ margin: '16px 0 0' }}>
          <ExinVoucherButton />
        </p>
      </>
    );
  }

  return (
    <>
      <ExinSelect
        options={countries}
        value={selectedCountry}
        // @ts-ignore
        // Something in react-select#Select demands onChange data to be `unknown`
        // TypeScript will complain, because our own data is in fact _not_ unknown.
        onChange={setSelectedCountryValue}
        placeholder={<Trans i18nKey="placeholderSelect">Select</Trans>}
      />
    </>
  );
};

export default StepBookingCountry;
